/**
 * Umschläge für Projekte.
 *
 * Wie im Regal einer Notizbuch-App: jedes Projekt bekommt einen Einband mit
 * eigener Farbe und eigenem Muster. Die Farben stehen bewusst fest und folgen
 * nicht dem Theme – ein Notizbuch behält seinen Einband, egal ob die App hell
 * oder dunkel ist.
 *
 * Die Muster sind reine CSS-Verläufe. Das hält die App klein, braucht keine
 * Bilddateien und bleibt in jeder Auflösung scharf.
 */

export interface CoverDef {
  name: string;
  /** Grundfarbe des Einbands. */
  bg: string;
  /** Farbe des Buchrückens am linken Rand. */
  spine: string;
  /** Musterfarbe. */
  ink: string;
  /** Art des Musters. */
  pattern: 'dots' | 'stripes' | 'grid' | 'arcs' | 'plain';
  /**
   * Echte Stoffstruktur als nahtlos kachelndes Foto (public/textures).
   * Ersetzt Grundfarbe und Muster; bg bleibt als Farbe, bis das Bild da ist.
   */
  texture?: string;
}

/** Name der reinen Zeichenflaeche – an einer Stelle, nicht dreimal getippt. */
export const DRAWING_CANVAS_NAME = 'Zeichenfläche Weiß';

/**
 * Ein vollstaendig einfarbiger Einband.
 *
 * Nimmt EINE Farbe fuer Grundflaeche, Ruecken und Muster und schaltet das
 * Muster ab. So kann ein dezentes Preset gar nicht versehentlich Kontrast
 * oder ein Raster bekommen – die Regel steht im Code, nicht in der Disziplin
 * beim Eintippen.
 */
function mono(name: string, color: string): CoverDef {
  return { name, bg: color, spine: color, ink: color, pattern: 'plain' };
}

export const COVERS: CoverDef[] = [
  { name: 'Tannengrün', bg: '#1f3d3a', spine: '#16302d', ink: '#7fc3a8', pattern: 'arcs' },
  { name: 'Koralle', bg: '#d84f5e', spine: '#2a2326', ink: '#ffd8cf', pattern: 'stripes' },
  { name: 'Safran', bg: '#e0a52e', spine: '#9aae7c', ink: '#7a4a1c', pattern: 'dots' },
  { name: 'Kobalt', bg: '#2f5aa8', spine: '#1d3b73', ink: '#a9c6f2', pattern: 'grid' },
  { name: 'Papier', bg: '#efe7d4', spine: '#d8c9a6', ink: '#b04a2f', pattern: 'dots' },
  { name: 'Aubergine', bg: '#4a3560', spine: '#332443', ink: '#cdb0e6', pattern: 'arcs' },
  { name: 'Terrakotta', bg: '#b5613c', spine: '#7d3f26', ink: '#f2d2b8', pattern: 'stripes' },
  { name: 'Graphit', bg: '#2b2f33', spine: '#1a1d20', ink: '#8d979f', pattern: 'grid' },
  /*
   * Ab hier neue Presets. Bleiben Teil desselben Systems: dieselbe Form,
   * dieselben Muster, nur andere Farben – kein zweiter Baukasten.
   */
  {
    // Sehr helles, warmes Weiss – hochwertiges Papier statt steriles Weiss.
    // Bewusst schwacher Kontrast zwischen bg/spine/ink: siehe DESIGNPRINZIP.
    name: 'Pastell Weiß',
    bg: '#faf7f0',
    spine: '#ece5d6',
    ink: '#e3dbca',
    pattern: 'dots',
  },
  { name: 'Perlgrau', bg: '#e7e6e1', spine: '#d3d1c9', ink: '#c7c4b8', pattern: 'grid' },
  { name: 'Puderrosa', bg: '#f2dfd9', spine: '#e0c3ba', ink: '#c98f7f', pattern: 'dots' },
  { name: 'Salbei', bg: '#dfe6d6', spine: '#c3cfb3', ink: '#7c9463', pattern: 'stripes' },
  { name: 'Mitternacht', bg: '#171a24', spine: '#0f1119', ink: '#4a5578', pattern: 'arcs' },
  { name: 'Editorial Schwarz', bg: '#1c1c1c', spine: '#111111', ink: '#8a8a8a', pattern: 'plain' },
  { name: 'Sandstein', bg: '#d9c6a5', spine: '#b89e73', ink: '#8a6b3f', pattern: 'grid' },

  /*
   * Die Zeichenflaeche.
   *
   * Reines Weiss, nichts darueber: kein Muster, kein Farbstich, keine
   * Durchsichtigkeit. Sie steht bewusst IN dieser Liste und ist kein
   * Sonderfall daneben – dadurch laesst sie sich speichern, wiederherstellen
   * und im Presetraster auswaehlen wie jeder andere Einband auch.
   *
   * bg, spine und ink sind absichtlich derselbe Wert: der Buchruecken darf
   * auf der Zeichenflaeche keinen Streifen hinterlassen.
   */
  { name: 'Zeichenfläche Weiß', bg: '#ffffff', spine: '#ffffff', ink: '#ffffff', pattern: 'plain' },

  /*
   * Dezente Pastelltoene.
   *
   * DESIGNPRINZIP: monochrom und ruhig. Jeder dieser Einbaende ist
   * vollstaendig einfarbig – Grundfarbe, Ruecken und Musterfarbe sind
   * derselbe Wert, das Muster ist "plain". Kein Verlauf, kein Raster, kein
   * harter Kontrast. Die Form kommt allein aus Schatten und Seitenkante,
   * die jeder Einband ohnehin hat. Damit fuegen sie sich in das vorhandene
   * Regal ein, statt ein zweites Designsystem aufzumachen.
   *
   * Die Farben liegen alle sehr hell und stark entsaettigt (Helligkeit um
   * 90 %, Saettigung unter 20 %) – naeher an gutem Papier als an einer
   * bunten Oberflaeche.
   */
  mono('Off-White', '#f7f5f1'),
  mono('Sand', '#efe7da'),
  mono('Beige', '#e9ded0'),
  mono('Salbeigrün', '#e2e8df'),
  mono('Himmelblau', '#e1e9f1'),
  mono('Graublau', '#dee3e9'),
  mono('Rosé', '#f2e7e4'),
  mono('Lavendel', '#e8e5f0'),
  mono('Pfirsich', '#f5e7dc'),
  mono('Mint', '#e1ede7'),

  /*
   * Buchleinen. Echte Stoffscans (nahtlos kachelnd, 720 px, public/textures)
   * statt CSS-Mustern – so sieht ein Einband wie gebundenes Leinen aus.
   * Immer hinten anfuegen: gespeichert wird die Position in dieser Liste.
   */
  { name: 'Leinen Grau', bg: '#828282', spine: '#5e5e5e', ink: '#828282', pattern: 'plain', texture: './textures/leinen-grau.jpg' },
  { name: 'Leinen Hell', bg: '#d6d6d4', spine: '#b9b9b6', ink: '#d6d6d4', pattern: 'plain', texture: './textures/leinen-hell.jpg' },
  { name: 'Leinen Oliv', bg: '#4a4a1c', spine: '#35350f', ink: '#4a4a1c', pattern: 'plain', texture: './textures/leinen-oliv.jpg' },
];

/** Position von "Zeichenfläche Weiß" – dem reinen Weiss zum Zeichnen. */
export function drawingCanvasIndex(): number {
  const index = COVERS.findIndex((c) => c.name === DRAWING_CANVAS_NAME);
  return index === -1 ? 0 : index;
}

/** Ist dieser Einband die reine Zeichenflaeche? */
export function isDrawingCanvas(index: number): boolean {
  return coverAt(index).name === DRAWING_CANVAS_NAME;
}

/** Sorgt dafür, dass ein gespeicherter Index immer auf einen Einband zeigt. */
export function coverAt(index: number): CoverDef {
  const count = COVERS.length;
  const safe = ((Math.trunc(index) % count) + count) % count;
  return COVERS[safe] as CoverDef;
}

/** Baut das CSS-Muster eines Einbands. */
export function coverArt(def: CoverDef): { backgroundImage: string; backgroundSize: string } {
  const c = def.ink;
  switch (def.pattern) {
    case 'dots':
      return {
        backgroundImage:
          `radial-gradient(circle at 30% 30%, ${c} 9%, transparent 10%),` +
          `radial-gradient(circle at 80% 70%, ${c} 6%, transparent 7%)`,
        backgroundSize: '44px 44px, 44px 44px',
      };
    case 'stripes':
      return {
        backgroundImage: `repeating-linear-gradient(48deg, ${c} 0 3px, transparent 3px 16px)`,
        backgroundSize: 'auto',
      };
    case 'grid':
      return {
        backgroundImage:
          `linear-gradient(${c} 1.5px, transparent 1.5px),` +
          `linear-gradient(90deg, ${c} 1.5px, transparent 1.5px)`,
        backgroundSize: '34px 34px, 34px 34px',
      };
    case 'arcs':
      return {
        backgroundImage: `repeating-radial-gradient(circle at 12% 88%, transparent 0 22px, ${c} 22px 24px)`,
        backgroundSize: 'auto',
      };
    case 'plain':
    default:
      return { backgroundImage: 'none', backgroundSize: 'auto' };
  }
}

/** Erzeugt das Einband-Element für ein Projekt. */
/**
 * Der Einband eines Projekts, so wie er wirklich aussieht.
 *
 * Ein selbst gezeichneter Einband liegt als Bild ueber dem Preset – dieselbe
 * Schichtung wie im Editor. Dadurch bleibt das Preset erhalten und man kann
 * jederzeit zurueckwechseln, ohne die Zeichnung zu verlieren.
 */
export function renderProjectCover(project: {
  cover: number;
  coverMode?: 'preset' | 'custom';
  coverArtwork?: string;
  coverImage?: string;
}): HTMLElement {
  const el = renderCover(project.cover);
  // Eigenes Bild: ueber dem Preset, unter der Zeichnung.
  if (project.coverImage) {
    const img = document.createElement('img');
    img.className = 'cover__image';
    img.alt = '';
    img.src = project.coverImage;
    el.append(img);
  }
  if (project.coverMode === 'custom' && project.coverArtwork) {
    const art = document.createElement('img');
    art.className = 'cover__artwork';
    art.alt = '';
    art.src = project.coverArtwork;
    el.append(art);
  }
  return el;
}

export function renderCover(index: number): HTMLElement {
  const def = coverAt(index);
  const art = coverArt(def);

  const cover = document.createElement('div');
  // Die reine Zeichenflaeche bekommt eine eigene Klasse: das CSS nimmt ihr
  // damit auch noch die angedeutete Seitenkante und das Muster weg, so dass
  // wirklich nichts als Weiss uebrig bleibt.
  cover.className = def.name === DRAWING_CANVAS_NAME ? 'cover cover--canvas' : 'cover';
  cover.style.background = def.bg;

  const spine = document.createElement('div');
  spine.className = 'cover__spine';
  spine.style.background = def.spine;

  const pattern = document.createElement('div');
  pattern.className = 'cover__art';
  pattern.style.backgroundImage = art.backgroundImage;
  pattern.style.backgroundSize = art.backgroundSize;
  if (def.texture) {
    // Stoff: das Foto traegt Farbe und Struktur, halbe Kachelgroesse fuer
    // scharfe Faeden auf dem Retina-Bildschirm.
    cover.style.backgroundImage = `url("${def.texture}")`;
    cover.style.backgroundSize = '360px 360px';
    spine.style.backgroundImage = `linear-gradient(90deg, rgba(0,0,0,0.28), rgba(0,0,0,0.12)), url("${def.texture}")`;
    spine.style.backgroundSize = 'auto, 360px 360px';
  }

  cover.append(spine, pattern);
  return cover;
}
